// Import API functions
import { getWishlist, addToWishlistApi, removeFromWishlistApi } from "../api";
import { devLog, devWarn } from "./devLog";

export const WISHLIST_EVENT = "proppins:wishlist-updated";

// Store to track which project IDs are villas/plots stored as apartments
// Format: { projectId: 'villa' | 'plot' }
let categoryTrackingMap = {};

// Load tracking from localStorage
const loadCategoryTracking = () => {
  try {
    const stored = localStorage.getItem('wishlist_category_mapping');
    categoryTrackingMap = stored ? JSON.parse(stored) : {};
  } catch (e) {
    categoryTrackingMap = {};
  }
};

// Save tracking to localStorage
const saveCategoryTracking = () => {
  try {
    localStorage.setItem('wishlist_category_mapping', JSON.stringify(categoryTrackingMap));
  } catch (e) {
    devWarn('Failed to save category tracking');
  }
};

// Track that a project ID is actually a villa/plot (even though stored as apartment)
const trackCategoryFallback = (projectId, originalCategory, itemDetails = {}) => {
  if (originalCategory !== 'apartment') {
    categoryTrackingMap[String(projectId)] = originalCategory;
    saveCategoryTracking();
    devLog(`Tracked wishlist category: project ${projectId} → ${originalCategory}`, itemDetails);
  }
};

// Get the original category for a project ID
const getOriginalCategory = (projectId) => {
  loadCategoryTracking();
  return categoryTrackingMap[String(projectId)];
};

const DEFAULT_STORE = {
  apartment: [],
  villa: [],
  plot: [],
};

const CATEGORY_MAP = {
  apartment: "apartment",
  apartments: "apartment",
  "apartment_buy": "apartment",
  "apartment-rent": "apartment",
  "apartment_rent": "apartment",
  "apartment_resale": "apartment",
  "apartments-resale": "apartment",
  "apts-resale": "apartment",
  "apts-resale-property-page": "apartment",
  "rents-apts": "apartment",
  "rents-apts-property-page": "apartment",
  villa: "villa",
  villas: "villa",
  "villa_buy": "villa",
  "villa-rent": "villa",
  "villa_rent": "villa",
  "villa_resale": "villa",
  "villas-resale": "villa",
  "villas-resale-property-page": "villa",
  "rents-villas": "villa",
  "rents-villas-property-page": "villa",
  plot: "plot",
  plots: "plot",
  "plot_buy": "plot",
  "plot-rent": "plot",
  "plot_rent": "plot",
  "plot_resale": "plot",
  "plots-resale": "plot",
  "plots-resale-property-page": "plot",
  "rents-plots": "plot",
};

export const resolveCategory = (value = "") => {
  if (!value) {
    return "apartment";
  }

  const normalized = String(value).toLowerCase().trim();

  // Direct match in category map
  if (CATEGORY_MAP[normalized]) {
    return CATEGORY_MAP[normalized];
  }

  // Partial match for better detection (order matters - check more specific first)
  if (normalized.includes('resale')) {
    // Resale properties map to their base type
    if (normalized.includes('villa')) {
      return "villa";
    }
    if (normalized.includes('plot')) {
      return "plot";
    }
    if (normalized.includes('apartment') || normalized.includes('apts')) {
      return "apartment";
    }
  }
  if (normalized.includes('villa')) {
    return "villa";
  }
  if (normalized.includes('plot')) {
    return "plot";
  }
  if (normalized.includes('apartment') || normalized.includes('apts')) {
    return "apartment";
  }

  return "apartment";
};

const cloneStore = (store) => ({
  apartment: [...(store?.apartment || [])],
  villa: [...(store?.villa || [])],
  plot: [...(store?.plot || [])],
});

const dispatchUpdate = (store) => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(WISHLIST_EVENT, {
        detail: { store, timestamp: Date.now() },
      })
    );
  }
};

// Get current user ID from localStorage
const getCurrentUserId = () => {
  try {
    const userStr = localStorage.getItem("auth_user") || localStorage.getItem("user");
    if (!userStr) return null;
    const user = JSON.parse(userStr);
    return user?.id || user?.user_id || null;
  } catch {
    return null;
  }
};

// Check if user is authenticated
const isAuthenticated = () => {
  try {
    const token = localStorage.getItem("auth_token");
    return !!token;
  } catch {
    return false;
  }
};

// In-memory cache for wishlist store
let cachedStore = null;
let isLoading = false;
let loadPromise = null;
let currentUserId = null; // Track current user to detect user changes - initialized on first load
/** Avoid duplicate force-refreshes (multiple hooks, React Strict Mode remount). */
let lastWishlistFetchedAt = 0;
const WISHLIST_REFRESH_COALESCE_MS = 900;

// Load wishlist from API
export const loadWishlistFromAPI = async (forceRefresh = false) => {
  // Check if user is authenticated
  if (!isAuthenticated()) {
    cachedStore = cloneStore(DEFAULT_STORE);
    currentUserId = null;
    lastWishlistFetchedAt = 0;
    return cachedStore;
  }

  const userId = getCurrentUserId();

  // If user changed, clear cache and force refresh
  if (currentUserId !== null && currentUserId !== userId) {
    cachedStore = null;
    loadPromise = null;
    lastWishlistFetchedAt = 0;
    forceRefresh = true;
  }

  currentUserId = userId;

  // Single in-flight load for all callers (parallel useWishlist mounts, etc.)
  if (loadPromise) {
    return loadPromise;
  }

  const now = Date.now();
  if (forceRefresh && cachedStore && now - lastWishlistFetchedAt < WISHLIST_REFRESH_COALESCE_MS) {
    return cachedStore;
  }

  if (forceRefresh) {
    cachedStore = null;
  }

  if (cachedStore) {
    return cachedStore;
  }

  isLoading = true;
  loadPromise = (async () => {
    try {
      devLog("Loading wishlist from API…", { userId: getCurrentUserId() });

      // Load wishlist for all categories
      let allResponse = [];
      try {
        // Try to load from each category
        const [aptResponse, villaResponse, plotResponse] = await Promise.all([
          getWishlist("apartment").catch(e => {
            devWarn("Failed to load apartment wishlist:", e.message);
            return { data: [] };
          }),
          getWishlist("villa").catch(e => {
            devWarn("Failed to load villa wishlist:", e.message);
            return { data: [] };
          }),
          getWishlist("plot").catch(e => {
            devWarn("Failed to load plot wishlist:", e.message);
            return { data: [] };
          })
        ]);

        // Combine responses
        allResponse = [
          ...(aptResponse?.data || []),
          ...(villaResponse?.data || []),
          ...(plotResponse?.data || [])
        ];
      } catch (error) {
        devWarn("Failed to load category-specific wishlists, trying generic endpoint:", error.message);
        try {
          const response = await getWishlist();
          allResponse = response?.data || response || [];
        } catch (e) {
          devWarn("Failed to load wishlist:", e.message);
          allResponse = [];
        }
      }

      const response = allResponse;
      devLog("Wishlist combined rows:", Array.isArray(response) ? response.length : "non-array");

      // Handle different response formats
      let data = null;
      if (response?.success && response?.data) {
        data = response.data;
      } else if (response?.data) {
        data = response.data;
      } else if (Array.isArray(response)) {
        data = response;
      } else if (response) {
        data = response;
      } else {
        data = [];
      }

      devLog("Wishlist data extracted:", Array.isArray(data) ? `${data.length} items` : typeof data);

      // Transform API response to our store format
      const store = cloneStore(DEFAULT_STORE);

      if (Array.isArray(data)) {
        devLog("Processing wishlist items:", data.length);
        data.forEach((item) => {
          // Resolve category with better detection
          // IMPORTANT: Check if property_type contains underscores (like "villa_buy", "apartment_rent")
          // and normalize to base category
          // Also check nested project data if property_type is missing
          let propertyType = item.property_type || item.propertyType || item.type || item.project_type || item.category;

          // If property_type is still missing, try to infer from project data or other fields
          if (!propertyType || propertyType.trim() === '') {
            // Check if there's nested project data
            if (item.project?.property_type) {
              propertyType = item.project.property_type;
            } else if (item.project?.type) {
              propertyType = item.project.type;
            } else if (item.details?.property_type) {
              propertyType = item.details.property_type;
            } else if (item.villa || item.villa_buy_detail || item.villa_rent_detail) {
              // If there's a 'villa' key or villa detail objects, it's likely a villa
              propertyType = 'villa';
            } else if (item.plot || item.plot_buy_detail || item.plot_rent_detail) {
              propertyType = 'plot';
            } else if (item.apartment && item.title && (item.title.toLowerCase().includes('villa') || item.title.toLowerCase().includes('villas'))) {
              // Sometimes API returns 'apartment' key but it's actually a villa based on title
              propertyType = 'villa';
            } else if (item.slug && item.slug.includes('villa')) {
              // Check slug for villa indicator
              propertyType = 'villa';
            } else if (item.slug && item.slug.includes('plot')) {
              propertyType = 'plot';
            }
          }

          const category = resolveCategory(propertyType);

          devLog(`Processing wishlist item from API - property_type: "${propertyType}", resolved category: "${category}"`);
          devLog(`Processing item ${item.project_id || item.id}: category=${category}, property_type=${item.property_type}, title=${item.title || item.property_title || item.name}`);

          if (store[category]) {
            // Transform API item to our format
            // IMPORTANT: The API response 'id' field is the wishlist entry ID (primary key of wishlist table)
            // The 'project_id' is the actual project being wishlisted
            // For deletion, we need to use project_id, but the API might also accept wishlist entry ID
            // CRITICAL: Extract project_id from multiple possible locations including nested structures
            // The API might return project data in different formats
            const extractedProjectId =
              item.project_id ||
              item.property_id ||
              item.projectId ||
              item.project?.id ||
              item.project?.project_id ||
              item.property?.project_id ||
              item.property?.id ||
              item.details?.project_id ||
              item.details?.id ||
              // Check if there's a nested project object with id
              (item.project && typeof item.project === 'object' ? (item.project.id || item.project.project_id) : null) ||
              null; // Use null instead of empty string

            // Convert to string only if we have a valid value (not empty string)
            const projectIdStr = extractedProjectId && String(extractedProjectId).trim() !== ''
              ? String(extractedProjectId).trim()
              : null;

            const wishlistItem = {
              // Primary ID for matching - use project_id as that's what we match against
              id: projectIdStr || String(item.id || ''),
              // Store project_id explicitly (this is what API needs for deletion)
              project_id: projectIdStr,
              // Store wishlist entry ID (the wishlist table's primary key - might be needed for some operations)
              wishlist_id: item.id || item.wishlist_id || item.wishlistId || null,
              title: item.title || item.property_title || item.name || item.project_title || "Untitled",
              builder: item.builder || item.by_label || item.company_name || item.developer || "",
              location: item.location || item.property_location || item.address || "",
              price: item.price || item.price_range || item.rent_per_month || item.price_from || item.details?.price_range || "",
              image: item.image || item.image_url || item.cover_image || item.cover_image_url || "",
              propertyType: category, // Normalized category (villa, apartment, plot)
              originalPropertyType: propertyType, // Store original property_type from API (e.g., "villa_buy")
              raw: item, // Store full raw item for debugging and reference
              timestamp: item.created_at || item.timestamp || Date.now(),
            };

            // Log for debugging - especially important for tracking project_id issues
            devLog(`Processing wishlist item from API:`, {
              title: wishlistItem.title,
              extractedProjectId: projectIdStr,
              wishlistId: wishlistItem.wishlist_id,
              category,
              apiItemId: item.id,
              apiProjectId: item.project_id,
              hasNestedProject: !!item.project,
              nestedProjectId: item.project?.id || item.project?.project_id
            });

            store[category].push(wishlistItem);
          } else {
            devWarn(`Unknown category for wishlist item: ${category}, item:`, item);
          }
        });
      } else if (typeof data === 'object' && data !== null) {
        // If API returns object with categories
        devLog("Processing object with categories");
        if (Array.isArray(data.apartment) || Array.isArray(data.apartments)) {
          store.apartment = (data.apartment || data.apartments).map(item => ({
            id: String(item.id || item.wishlist_id || item.wishlistId || item.project_id || item.property_id || ''),
            project_id: String(item.project_id || item.property_id || item.projectId || item.id || ''),
            wishlist_id: item.id || item.wishlist_id || item.wishlistId || null,
            title: item.title || item.property_title || item.name || item.project_title || "Untitled",
            builder: item.builder || item.by_label || item.developer || "",
            location: item.location || item.property_location || item.address || "",
            price: item.price || item.price_range || item.price_from || "",
            image: item.image || item.image_url || item.cover_image || item.cover_image_url || "",
            propertyType: 'apartment',
            raw: item,
            timestamp: item.created_at || Date.now(),
          }));
        }
        if (Array.isArray(data.villa) || Array.isArray(data.villas)) {
          store.villa = (data.villa || data.villas).map(item => ({
            id: String(item.id || item.wishlist_id || item.wishlistId || item.project_id || item.property_id || ''),
            project_id: String(item.project_id || item.property_id || item.projectId || item.id || ''),
            wishlist_id: item.id || item.wishlist_id || item.wishlistId || null,
            title: item.title || item.property_title || item.name || item.project_title || "Untitled",
            builder: item.builder || item.by_label || item.developer || "",
            location: item.location || item.property_location || item.address || "",
            price: item.price || item.price_range || item.price_from || "",
            image: item.image || item.image_url || item.cover_image || item.cover_image_url || "",
            propertyType: 'villa',
            raw: item,
            timestamp: item.created_at || Date.now(),
          }));
        }
        if (Array.isArray(data.plot) || Array.isArray(data.plots)) {
          store.plot = (data.plot || data.plots).map(item => ({
            id: String(item.id || item.wishlist_id || item.wishlistId || item.project_id || item.property_id || ''),
            project_id: String(item.project_id || item.property_id || item.projectId || item.id || ''),
            wishlist_id: item.id || item.wishlist_id || item.wishlistId || null,
            title: item.title || item.property_title || item.name || item.project_title || "Untitled",
            builder: item.builder || item.by_label || item.developer || "",
            location: item.location || item.property_location || item.address || "",
            price: item.price || item.price_range || item.price_from || "",
            image: item.image || item.image_url || item.cover_image || item.cover_image_url || "",
            propertyType: 'plot',
            raw: item,
            timestamp: item.created_at || Date.now(),
          }));
        }
      }

      devLog("Wishlist store after transformation:", {
        apartment: store.apartment.length,
        villa: store.villa.length,
        plot: store.plot.length,
      });

      // POST-PROCESS: Remap items to their original categories if they were tracked as villa/plot
      devLog("Applying wishlist category remapping…");
      loadCategoryTracking(); // Load the tracking map

      const remappedStore = cloneStore(DEFAULT_STORE);
      Object.entries(store).forEach(([category, items]) => {
        items.forEach((item) => {
          const projectId = String(item.project_id || item.id || '');
          const originalCategory = getOriginalCategory(projectId);

          if (originalCategory && originalCategory !== category) {
            devLog(`Remap wishlist row ${projectId}: ${category} → ${originalCategory}`);
            remappedStore[originalCategory].push(item);
          } else {
            // Keep in original category
            remappedStore[category].push(item);
          }
        });
      });

      devLog("Wishlist store after remapping:", {
        apartment: remappedStore.apartment.length,
        villa: remappedStore.villa.length,
        plot: remappedStore.plot.length,
      });

      cachedStore = remappedStore;
      lastWishlistFetchedAt = Date.now();
      return remappedStore;
    } catch (error) {
      devWarn("Failed to load wishlist from API:", error);
      devWarn("Error details:", {
        message: error.message,
        response: error.response,
        status: error.response?.status,
      });

      // If error is 401 (unauthorized), user might not be logged in
      if (error?.response?.status === 401 || error?.response?.data?.status === 401) {
        devLog("Wishlist load returned 401 — clearing local wishlist state");
        cachedStore = cloneStore(DEFAULT_STORE);
        currentUserId = null;
        lastWishlistFetchedAt = 0;
      } else {
        // Return cached store if available, otherwise empty
        cachedStore = cachedStore || cloneStore(DEFAULT_STORE);
      }
      return cachedStore;
    } finally {
      isLoading = false;
      loadPromise = null;
    }
  })();

  return loadPromise;
};

// Helper to validate an ID
const isValidId = (val) => {
  if (!val) return false;
  const str = String(val).trim();
  return str !== '' && str !== 'null' && str !== 'undefined';
};

// Helper to extract project_id from a wishlist item
const extractProjectId = (item) => {
  if (!item) return null;

  const rawItem = item.raw || {};
  let projectId = null;

  // Try different sources in priority order
  if (isValidId(item.project_id)) {
    projectId = item.project_id;
  } else if (isValidId(rawItem.project_id)) {
    projectId = rawItem.project_id;
  } else if (isValidId(item.apartment_buy_detail?.project_id)) {
    projectId = item.apartment_buy_detail.project_id;
  } else if (isValidId(item.apartment_rent_detail?.project_id)) {
    projectId = item.apartment_rent_detail.project_id;
  } else if (isValidId(rawItem.property_id)) {
    projectId = rawItem.property_id;
  } else if (isValidId(rawItem.project?.id)) {
    projectId = rawItem.project.id;
  } else if (isValidId(rawItem.project?.project_id)) {
    projectId = rawItem.project.project_id;
  } else if (isValidId(rawItem.property?.project_id)) {
    projectId = rawItem.property.project_id;
  } else if (isValidId(rawItem.property?.id)) {
    projectId = rawItem.property.id;
  } else if (isValidId(rawItem.details?.project_id)) {
    projectId = rawItem.details.project_id;
  } else if (isValidId(item.details?.project_id)) {
    projectId = item.details.project_id;
  } else if (isValidId(rawItem.apartment_buy_detail?.project_id)) {
    projectId = rawItem.apartment_buy_detail.project_id;
  } else if (isValidId(rawItem.apartment_rent_detail?.project_id)) {
    projectId = rawItem.apartment_rent_detail.project_id;
  } else if (isValidId(item.id) && /^\d+$/.test(String(item.id))) {
    projectId = item.id;
  } else if (isValidId(rawItem.id) && /^\d+$/.test(String(rawItem.id))) {
    projectId = rawItem.id;
  }

  // If still no project_id found, try using wishlist_id as last resort
  if (!projectId || !isValidId(projectId)) {
    if (isValidId(item.wishlist_id)) {
      projectId = item.wishlist_id;
    } else if (isValidId(rawItem.id)) {
      projectId = rawItem.id;
    } else if (isValidId(item.id)) {
      projectId = item.id;
    }
  }

  return projectId || null;
};

export const getWishlistStore = async (forceRefresh = false) => {
  // Always check authentication first
  if (!isAuthenticated()) {
    cachedStore = cloneStore(DEFAULT_STORE);
    return cachedStore;
  }

  // If forcing refresh or no cache, load from API
  if (forceRefresh || !cachedStore) {
    return await loadWishlistFromAPI(forceRefresh);
  }

  // Return cached store
  return cachedStore;
};

export const getWishlistItems = async (type = "apartment") => {
  const store = await getWishlistStore();
  return store[resolveCategory(type)];
};

export const getWishlistCount = async () => {
  const store = await getWishlistStore();
  return (
    (store.apartment?.length || 0) +
    (store.villa?.length || 0) +
    (store.plot?.length || 0)
  );
};

export const buildWishlistPayload = (item = {}, fallbackType = "apartment") => {
  if (!item) return null;

  // CRITICAL: Prioritize project_id as API expects it (must be numeric)
  // We should ONLY use explicit project_id fields, not generic 'id' which might be wrong
  // Check multiple possible locations for the project_id
  const projectId =
    item.project_id ||
    item.projectId ||
    item.raw?.project_id ||
    item.raw?.projectId ||
    // Check nested details FIRST before falling back to generic ID
    item.details?.project_id ||
    item.raw?.details?.project_id ||
    item.apartment_buy_detail?.project_id ||
    item.apartment_rent_detail?.project_id ||
    item.raw?.apartment_buy_detail?.project_id ||
    item.raw?.apartment_rent_detail?.project_id ||
    item.raw?.villa_buy_detail?.project_id ||
    item.villa_buy_detail?.project_id ||
    item.raw?.id ||  // API response 'id' field is usually the project_id
    item.raw?.villa_buy_detail?.project_id ||  // For villas
    item.raw?.details?.project_id ||
    item.details?.project_id ||
    item.villa_buy_detail?.project_id;  // For villas

  // Fallback to item.id ONLY if we're confident it's a project ID (numeric and looks valid)
  // But this should be avoided as item.id might be a listing ID or other identifier
  const fallbackToId = !projectId && item.id && /^\d+$/.test(String(item.id));
  const id = projectId || (fallbackToId ? item.id : null);

  // If no numeric ID found, try slug as fallback (but API might reject it)
  const fallbackId = item.slug || item.raw?.slug;

  if (!id && !fallbackId) {
    devWarn("Cannot build wishlist payload: no valid project_id found", {
      item,
      title: item.title,
      hasProjectId: !!item.project_id,
      hasProjectIdRaw: !!item.raw?.project_id,
      hasId: !!item.id,
      itemId: item.id,
      hasSlug: !!item.slug,
      slug: item.slug
    });
    return null;
  }

  // Use numeric project_id if available, otherwise use slug
  const finalId = id || fallbackId;

  // Log for debugging villas
  if (fallbackType === "villa" || item.propertyType === "villa") {
    devLog("buildWishlistPayload - Villa item:", {
      item,
      id,
      fallbackId,
      finalId,
      hasProjectId: !!item.project_id,
      hasId: !!item.id,
      hasRaw: !!item.raw,
      rawId: item.raw?.id,
      rawProjectId: item.raw?.project_id
    });
  }

  // Validate that we have a valid ID
  if (!finalId) {
    devWarn("buildWishlistPayload - No valid ID found for villa:", item);
    return null;
  }

  return {
    id: String(finalId), // Use finalId which includes fallback to slug if needed
    title: item.title || item.name || item.project_title || "Untitled",
    builder: item.by_label || item.builder || item.user?.company_name || item.developer || "",
    location: item.location || item.details?.location || item.address || "",
    price: item.price_range || item.details?.price_range || item.price_from || item.price || "",
    image: item.image || item.image_url || item.details?.image_url || item.cover_image || "",
    propertyType: fallbackType,
    raw: item,
    timestamp: Date.now(),
  };
};

export const addToWishlist = async (item, type = null) => {
  // Resolve category with better detection for villas and plots
  // CRITICAL: Prioritize explicit type parameter (when propertyType="villa" is passed)
  // Then check item properties, then fallback to apartment
  const category = resolveCategory(
    type ||  // Explicit type parameter takes highest priority
    item?.property_type ||
    item?.propertyType ||
    item?.type ||
    item?.raw?.property_type ||
    item?.raw?.propertyType ||
    "apartment"  // Default fallback only if nothing else found
  );
  devLog("=== ADD TO WISHLIST ===");
  devLog("Item:", item);
  devLog("Item._debug:", item?._debug);
  devLog("Type parameter (explicit):", type);
  devLog("Item property_type:", item?.property_type);
  devLog("Item propertyType:", item?.propertyType);
  devLog("Resolved category:", category);

  // Extra validation: if type was explicitly "villa" but category resolved to something else, log warning
  if (type && String(type).toLowerCase().includes('villa') && category !== 'villa') {
    devWarn("WARNING: Type parameter is 'villa' but category resolved to:", category);
  }

  const payload = buildWishlistPayload(item, category);
  if (!payload) {
    devWarn("Cannot add to wishlist: missing payload", item);
    return await getWishlistStore();
  }

  // Extract project_id - this is what the API needs
  // CRITICAL: Use ONLY explicit project_id fields, not generic 'id' which might be wrong
  const projectId = String(
    item.project_id ||
    item.projectId ||
    item.raw?.project_id ||
    item.raw?.projectId ||
    item.raw?.id ||  // API response 'id' field is usually the project_id
    item.raw?.villa_buy_detail?.project_id ||  // For villas
    item.raw?.details?.project_id ||
    item.details?.project_id ||
    item.villa_buy_detail?.project_id ||  // For villas
    // Fallback to payload.id only if it exists and is numeric (from buildWishlistPayload which validates it)
    (payload.id && /^\d+$/.test(String(payload.id)) ? payload.id : null) ||
    // Last resort: use item.id only if numeric (but log warning)
    (item.id && /^\d+$/.test(String(item.id)) ? item.id : null)
  );

  // Validate project_id is not empty or invalid
  if (!projectId || projectId === 'null' || projectId === 'undefined' || projectId.trim() === '') {
    devWarn("=== ADD TO WISHLIST - MISSING PROJECT ID ===");
    devWarn("Item:", item);
    devWarn("Item._debug:", item?._debug);
    devWarn("Item structure:", {
      hasProjectId: !!item.project_id,
      projectId: item.project_id,
      hasProjectIdRaw: !!item.raw?.project_id,
      rawProjectId: item.raw?.project_id,
      hasRawId: !!item.raw?.id,
      rawId: item.raw?.id,
      hasId: !!item.id,
      itemId: item.id,
      hasSlug: !!item.slug,
      slug: item.slug,
      category
    });
    throw new Error(`Cannot add to wishlist: missing project ID for ${item.title || 'property'}. Please refresh the page and try again.`);
  }

  // Log detailed info for debugging
  devLog("=== ADD TO WISHLIST - ID EXTRACTION ===");
  devLog("Item title:", item.title);
  devLog("Category:", category);
  devLog("Item structure:", {
    hasProjectId: !!item.project_id,
    projectId: item.project_id,
    hasProjectIdRaw: !!item.raw?.project_id,
    rawProjectId: item.raw?.project_id,
    hasRawId: !!item.raw?.id,
    rawId: item.raw?.id,
    hasId: !!item.id,
    itemId: item.id,
    hasSlug: !!item.slug,
    slug: item.slug
  });
  devLog("Extracted project_id for API:", projectId);
  devLog("Payload ID:", payload.id);

  try {
    // Validate category is correct
    if (!['apartment', 'villa', 'plot'].includes(category)) {
      devWarn("INVALID CATEGORY:", category);
      throw new Error(`Invalid category: ${category}. Must be apartment, villa, or plot`);
    }

    // Prepare API payload - API expects project_id (not property_id)
    const apiPayload = {
      project_id: projectId, // Use extracted project_id
      property_type: category, // CRITICAL: Must be apartment, villa, or plot
      property_slug: item.slug || item.id || projectId,
      title: payload.title,
      builder: payload.builder,
      location: payload.location,
      price: payload.price,
      image: payload.image,
    };

    devLog("=== API PAYLOAD ===");
    devLog("Full payload:", JSON.stringify(apiPayload, null, 2));
    devLog("Property type:", apiPayload.property_type, "(", category, ")");
    devLog("Project ID:", apiPayload.project_id);
    devLog("API endpoint: POST /wishlist");
    devLog("Auth token present:", !!localStorage.getItem("auth_token"));
    devLog("💾 SAVING TO CATEGORY:", category);

    let response;
    try {
      response = await addToWishlistApi(apiPayload);
    } catch (apiError) {
      // API threw an error - check if we should retry with apartment
      const errorMsg = apiError?.message || String(apiError) || "Failed to add to wishlist";
      devWarn("❌ API error:", errorMsg);

      // SMART FALLBACK: If we tried to add to villa/plot but it's actually an apartment, retry
      const shouldRetryAsApartment =
        (category === 'villa' && errorMsg.includes("not of type 'villa'") && errorMsg.includes("'apartment'")) ||
        (category === 'plot' && errorMsg.includes("not of type 'plot'") && errorMsg.includes("'apartment'"));

      if (shouldRetryAsApartment) {
        devWarn(`⚠️ ${category} project is actually stored as apartment - retrying with apartment category`);

        // Re-attempt with apartment category
        const apartmentPayload = {
          project_id: apiPayload.project_id,
          property_type: 'apartment',
          property_slug: apiPayload.property_slug,
          title: apiPayload.title,
          builder: apiPayload.builder,
          location: apiPayload.location,
          price: apiPayload.price,
          image: apiPayload.image,
        };

        devLog("🔄 Retrying with apartment category:", apartmentPayload);
        try {
          response = await addToWishlistApi(apartmentPayload);
          devLog("✅ Retry successful:", JSON.stringify(response, null, 2));
          // Track that this project is actually a villa/plot stored as apartment
          devLog(`📍 About to track - projectId: ${apiPayload.project_id}, category: ${category}, title: ${apiPayload.title}`);
          trackCategoryFallback(apiPayload.project_id, category, {
            title: apiPayload.title,
            location: apiPayload.location,
            price: apiPayload.price,
          });
          devLog(`📍 Successfully tracked - projectId: ${apiPayload.project_id} as ${category}`);
        } catch (retryError) {
          throw new Error(retryError?.message || "Failed to add to wishlist after retry");
        }
      } else {
        throw apiError;
      }
    }

    devLog("✅ Add to wishlist API response (full):", JSON.stringify(response, null, 2));

    // Check if the response indicates success
    if (response?.success === false) {
      const errorMsg = response?.message || "Failed to add to wishlist";
      devWarn("API returned success=false:", errorMsg);
      throw new Error(errorMsg);
    }

    // Wait a bit to ensure database is updated
    await new Promise(resolve => setTimeout(resolve, 500));

    // Update cache from API; do not dispatch here – the hook will refresh and dispatch
    // so the UI is not overwritten by a potentially stale list (add may not be visible yet)
    cachedStore = null; // Clear cache to force fresh load
    const store = await loadWishlistFromAPI(true);
    devLog("Wishlist refreshed after add:", {
      apartment: store.apartment.length,
      villa: store.villa.length,
      plot: store.plot.length,
      total: store.apartment.length + store.villa.length + store.plot.length
    });
    cachedStore = store; // Update cache only; hook will dispatch after its refresh

    return store;
  } catch (error) {
    devWarn("Failed to add to wishlist:", error);
    devWarn("Error details:", {
      message: error.message,
      response: error.response,
      status: error.response?.status,
      data: error.response?.data,
    });

    // Don't update optimistically if it's an auth error
    if (error?.response?.status === 401) {
      throw new Error("Please login to save properties to wishlist");
    }

    // Try to update cache optimistically for other errors
    const store = cachedStore || await getWishlistStore();
    const existing = store[category] || [];
    if (!existing.some((entry) => entry.id === payload.id)) {
      store[category] = [payload, ...existing];
      cachedStore = store;
      dispatchUpdate(store);
      devLog("Updated wishlist optimistically");
    }
    throw error; // Re-throw so component can handle it
  }
};

export const removeFromWishlist = async (id, type = "apartment") => {
  const category = resolveCategory(type);

  try {
    // Get the current store to find the item and extract the correct ID
    const currentStore = cachedStore || await getWishlistStore();
    const items = currentStore[category] || [];

    // Find the item by id (could be project_id, wishlist_id, or slug)
    // Try multiple matching strategies to ensure we find the item
    const searchId = String(id || '').trim();
    const itemToDelete = items.find(
      (entry) => {
        const entryId = String(entry.id || '').trim();
        const entryProjectId = String(entry.project_id || '').trim();
        const entryWishlistId = String(entry.wishlist_id || '').trim();
        const entrySlug = String(entry.slug || entry.raw?.slug || '').trim();

        if (!searchId) return false;

        // Try exact matches first
        return entryId === searchId ||
          entryProjectId === searchId ||
          entryWishlistId === searchId ||
          entrySlug === searchId ||
          // Also check if entry.id matches (might be the project_id stored as id)
          (entryProjectId && entryProjectId === searchId);
      }
    );

    // If item not found, throw error with helpful message
    if (!itemToDelete) {
      devWarn("Item not found in wishlist store for deletion:", {
        providedId: id,
        category,
        availableItems: items.map(i => ({
          id: i.id,
          project_id: i.project_id,
          wishlist_id: i.wishlist_id,
          title: i.title
        }))
      });
      throw new Error("Item not found in wishlist. Please refresh the page and try again.");
    }

    // Get the raw item data for extracting project_id
    const rawItem = itemToDelete?.raw || {};

    // Try multiple sources to find project_id
    // Priority: item.project_id > raw.project_id > nested project.id > item.id (if numeric) > raw.id (if numeric) > wishlist_id
    let deleteProjectId = null;

    // Helper to check if value is valid (not null, undefined, or empty string)
    const isValidId = (val) => {
      if (!val) return false;
      const str = String(val).trim();
      return str !== '' && str !== 'null' && str !== 'undefined';
    };

    // Try different sources in priority order
    if (isValidId(itemToDelete.project_id)) {
      deleteProjectId = itemToDelete.project_id;
    } else if (isValidId(rawItem.project_id)) {
      deleteProjectId = rawItem.project_id;
    } else if (isValidId(rawItem.property_id)) {
      deleteProjectId = rawItem.property_id;
    } else if (isValidId(rawItem.project?.id)) {
      deleteProjectId = rawItem.project.id;
    } else if (isValidId(rawItem.project?.project_id)) {
      deleteProjectId = rawItem.project.project_id;
    } else if (isValidId(rawItem.property?.project_id)) {
      deleteProjectId = rawItem.property.project_id;
    } else if (isValidId(rawItem.property?.id)) {
      deleteProjectId = rawItem.property.id;
    } else if (isValidId(rawItem.details?.project_id)) {
      deleteProjectId = rawItem.details.project_id;
    } else if (isValidId(itemToDelete.id) && /^\d+$/.test(String(itemToDelete.id))) {
      deleteProjectId = itemToDelete.id;
    } else if (isValidId(rawItem.id) && /^\d+$/.test(String(rawItem.id))) {
      deleteProjectId = rawItem.id;
    }

    // If still no project_id found, try using wishlist_id as last resort
    // (Some APIs might accept wishlist entry ID for deletion)
    if (!deleteProjectId || !isValidId(deleteProjectId)) {
      devWarn("No project_id found, attempting to use wishlist_id for deletion:", {
        wishlist_id: itemToDelete.wishlist_id,
        item: itemToDelete,
        rawItem: rawItem
      });
      if (isValidId(itemToDelete.wishlist_id)) {
        deleteProjectId = itemToDelete.wishlist_id;
      } else if (isValidId(rawItem.id)) {
        deleteProjectId = rawItem.id;
      } else if (isValidId(itemToDelete.id)) {
        deleteProjectId = itemToDelete.id;
      }
    }

    // Final validation - ensure we have something to delete
    if (!deleteProjectId || !isValidId(deleteProjectId)) {
      devWarn("Cannot delete: no valid project_id or wishlist_id found in wishlist item", {
        item: itemToDelete,
        rawItem: rawItem,
        availableFields: Object.keys(itemToDelete),
        rawFields: Object.keys(rawItem),
        itemId: itemToDelete.id,
        projectId: itemToDelete.project_id,
        wishlistId: itemToDelete.wishlist_id
      });
      throw new Error("Cannot delete item: missing project ID. Please refresh the page and try again.");
    }

    devLog("Removing from wishlist via API:", {
      originalId: id,
      deleteProjectId,
      category,
      itemDetails: {
        id: itemToDelete.id,
        project_id: itemToDelete.project_id,
        wishlist_id: itemToDelete.wishlist_id,
        title: itemToDelete.title,
        rawProjectId: rawItem.project_id,
        rawId: rawItem.id,
        rawPropertyType: rawItem.property_type,
        extractedProjectId: deleteProjectId
      }
    });
    devLog("Auth token present:", !!localStorage.getItem("auth_token"));

    // Use extracted project_id for deletion (API expects project_id)
    // IMPORTANT: When adding, we use normalized category (villa, apartment, plot) for property_type
    // So when deleting, we should also use the normalized category, not the original property_type format
    // This ensures consistency with what was stored in the database
    const deletePropertyType = category; // Use normalized category (matches what we use when adding)

    devLog("Deleting with property_type:", deletePropertyType, "(category:", category, ")");
    devLog("Deleting project_id:", deleteProjectId);

    let response;
    try {
      response = await removeFromWishlistApi(String(deleteProjectId), deletePropertyType);
    } catch (apiError) {
      // API threw an error - check if we should retry with apartment
      const errorMsg = apiError?.message || String(apiError) || "Failed to remove from wishlist";
      devWarn("❌ API error:", errorMsg);

      // SMART FALLBACK: If we tried to delete from villa/plot but it's actually an apartment, retry
      const shouldRetryAsApartment =
        (deletePropertyType === 'villa' && errorMsg.includes("not of type 'villa'") && errorMsg.includes("'apartment'")) ||
        (deletePropertyType === 'plot' && errorMsg.includes("not of type 'plot'") && errorMsg.includes("'apartment'"));

      if (shouldRetryAsApartment) {
        devWarn(`⚠️ ${deletePropertyType} project is actually stored as apartment - retrying delete with apartment category`);
        try {
          response = await removeFromWishlistApi(String(deleteProjectId), 'apartment');
          devLog("✅ Retry delete successful:", JSON.stringify(response, null, 2));
        } catch (retryError) {
          throw new Error(retryError?.message || "Failed to remove from wishlist after retry");
        }
      } else {
        throw apiError;
      }
    }

    devLog("Remove from wishlist API response (full):", JSON.stringify(response, null, 2));

    // Check if the response indicates success
    if (response?.success === false) {
      const errorMsg = response?.message || response?.error || "Failed to remove from wishlist";
      devWarn("API returned success=false:", errorMsg);
      throw new Error(errorMsg);
    }

    // Wait a bit to ensure database is updated
    await new Promise(resolve => setTimeout(resolve, 800));

    // Force refresh from API to get the latest state (after removal)
    cachedStore = null; // Clear cache to force fresh load
    currentUserId = null; // Reset user ID to force fresh load
    const store = await loadWishlistFromAPI(true);
    devLog("Wishlist refreshed after remove:", {
      apartment: store.apartment.length,
      villa: store.villa.length,
      plot: store.plot.length,
      total: store.apartment.length + store.villa.length + store.plot.length
    });
    cachedStore = store; // Update cache
    dispatchUpdate(store); // Dispatch event to update all components

    return store;
  } catch (error) {
    devWarn("Failed to remove from wishlist:", error);
    devWarn("Error details:", {
      message: error.message,
      response: error.response,
      status: error.response?.status,
      data: error.response?.data,
    });

    // Don't update optimistically if it's an auth error
    if (error?.response?.status === 401) {
      throw new Error("Please login to manage wishlist");
    }

    // Try to update cache optimistically for other errors
    const store = cachedStore || await getWishlistStore();
    store[category] = (store[category] || []).filter(
      (entry) =>
        entry.id !== String(id) &&
        entry.project_id !== String(id) &&
        entry.wishlist_id !== String(id) &&
        entry.slug !== String(id)
    );
    cachedStore = store;
    dispatchUpdate(store);
    throw error; // Re-throw so component can handle it
  }
};

export const clearWishlist = async (type = "apartment") => {
  const category = resolveCategory(type);
  const store = cachedStore || await getWishlistStore();

  // Remove all items of this category via API
  const items = store[category] || [];
  devLog(`🗑️  Clearing ${items.length} ${category} items from wishlist`);

  for (const item of items) {
    try {
      // Use robust project_id extraction
      const projectId = extractProjectId(item);

      if (!projectId) {
        devWarn(`⚠️  Cannot extract project ID from ${category} item:`, item);
        continue;
      }

      devLog(`🗑️  Removing ${category} item (project_id: ${projectId}) from wishlist via API`);
      await removeFromWishlistApi(projectId, category);
    } catch (error) {
      devWarn(`❌ Failed to remove item from wishlist:`, error);
    }
  }

  // Wait a bit to ensure database is updated
  await new Promise(resolve => setTimeout(resolve, 500));

  // Force refresh from API to get the latest state
  cachedStore = null; // Clear cache to force fresh load
  const refreshedStore = await loadWishlistFromAPI(true);
  devLog("✅ Wishlist refreshed after clear:", {
    apartment: refreshedStore.apartment.length,
    villa: refreshedStore.villa.length,
    plot: refreshedStore.plot.length,
    total: refreshedStore.apartment.length + refreshedStore.villa.length + refreshedStore.plot.length
  });
  cachedStore = refreshedStore; // Update cache
  dispatchUpdate(refreshedStore); // Dispatch event to update all components

  return refreshedStore;
};

export const clearAllWishlist = async () => {
  const store = cachedStore || await getWishlistStore();

  devLog("🗑️  Clearing all wishlist items");

  // Remove all items via API
  for (const category of ['apartment', 'villa', 'plot']) {
    const items = store[category] || [];
    devLog(`🗑️  Clearing ${items.length} ${category} items`);

    for (const item of items) {
      try {
        // Use robust project_id extraction
        const projectId = extractProjectId(item);

        if (!projectId) {
          devWarn(`⚠️  Cannot extract project ID from ${category} item:`, item);
          continue;
        }

        devLog(`🗑️  Removing ${category} item (project_id: ${projectId}) from wishlist via API`);
        await removeFromWishlistApi(projectId, category);
      } catch (error) {
        devWarn(`❌ Failed to remove item from wishlist:`, error);
      }
    }
  }

  // Wait a bit to ensure database is updated
  await new Promise(resolve => setTimeout(resolve, 800));

  // Force refresh from API to get the latest state
  cachedStore = null; // Clear cache to force fresh load
  currentUserId = null; // Reset user ID to force fresh load
  const refreshedStore = await loadWishlistFromAPI(true);
  devLog("✅ Wishlist refreshed after clear all:", {
    apartment: refreshedStore.apartment.length,
    villa: refreshedStore.villa.length,
    plot: refreshedStore.plot.length,
    total: refreshedStore.apartment.length + refreshedStore.villa.length + refreshedStore.plot.length
  });
  cachedStore = refreshedStore; // Update cache
  dispatchUpdate(refreshedStore); // Dispatch event to update all components

  return refreshedStore;
};

// Force refresh from API
export const refreshWishlist = async () => {
  cachedStore = null;
  currentUserId = null;
  lastWishlistFetchedAt = 0;
  return await loadWishlistFromAPI(true);
};

// Clear wishlist cache (called on logout)
export const clearWishlistCache = () => {
  cachedStore = null;
  currentUserId = null;
  isLoading = false;
  loadPromise = null;
  lastWishlistFetchedAt = 0;
  dispatchUpdate(cloneStore(DEFAULT_STORE));
};

// Sync optimistic store to cache and notify all listeners (so heart + count update on first click)
export const setWishlistCacheOptimistic = (store) => {
  if (store && typeof store === "object") {
    cachedStore = cloneStore(store);
    dispatchUpdate(cachedStore);
  }
};

